import atmosphere from 'atmosphere.js'
import { useCallback, useEffect, useRef } from 'react'
import { RateLimiter } from 'util/RateLimiter'

import { useAtmosphereTransportCache } from './atmosphere/useAtmosphereTransportCache'

export type AtmosphereReconnectPolicy = 'giveUp' | 'retryForever'

export interface AtmosphereVariables<T> {
  topic: string
  onMessage?(body: T, response, push: (message: T) => void): any
  onConnect?(): void
  onConnectionFailure?(): void
  // Ao esgotar a reconexão (loop detectado ou transportes esgotados): 'giveUp' desconecta (padrão, #28006), 'retryForever' retenta com backoff
  reconnectPolicy?: AtmosphereReconnectPolicy
}

const url = `${window.location.protocol}//${window.location.host}/atmosphere/`

// Loop detection and reconnection attempt due to https://github.com/laboratoriobridge/pec/issues/28006
const rateLimiter = new RateLimiter({ windowMs: 1000, maxEvents: 3 })
export const LOOP_BACKOFF_MS = 3000

export default function useAtmosphere<T>({
  topic,
  onMessage,
  onConnect,
  onConnectionFailure,
  reconnectPolicy = 'giveUp',
}: AtmosphereVariables<T>) {
  const connected = useRef(false)
  const subSocketRef = useRef<any>(null)
  const configRef = useRef<any>(null)
  const reconnectAttempts = useRef(0)
  const backoffTimer = useRef<ReturnType<typeof setTimeout>>()

  const { currentTransport, setCurrentTransport } = useAtmosphereTransportCache()

  const disconnect = useCallback(() => {
    connected.current = false
    atmosphere.unsubscribeUrl(url + topic)
    subSocketRef.current = null
  }, [topic])

  const push = useCallback((message: T) => {
    if (connected.current) {
      subSocketRef.current?.push(JSON.stringify(message))
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Atmosphere - Attempted to push message to topic ${configRef.current?.url} while not connected.`)
    }
  }, [])

  const connect = useCallback(() => {
    disconnect()
    subSocketRef.current = atmosphere.subscribe(configRef.current)
  }, [disconnect])

  // Funções recebidas por props, devem usar o useCallback + useEffect para atualizar os handlers do atmosphere conforme essas funcões mudam

  const handleOnMessage = useCallback(
    (request) => {
      if (!request.responseBody) return
      connected.current = true
      reconnectAttempts.current = 0
      onMessage?.(JSON.parse(request.responseBody), request, push)
    },
    [onMessage, push]
  )

  useEffect(() => {
    if (subSocketRef.current) {
      subSocketRef.current.request.onMessage = handleOnMessage
    }
  }, [handleOnMessage])

  const handleOnOpen = useCallback(() => {
    reconnectAttempts.current = 0
    if (backoffTimer.current) {
      clearTimeout(backoffTimer.current)
      backoffTimer.current = undefined
    }
    connected.current = true
    onConnect?.()
  }, [onConnect])

  useEffect(() => {
    if (subSocketRef.current) {
      subSocketRef.current.request.onOpen = handleOnOpen
    }
  }, [handleOnOpen])

  const scheduleReconnect = useCallback(() => {
    disconnect()
    reconnectAttempts.current = 0
    if (backoffTimer.current) clearTimeout(backoffTimer.current)
    backoffTimer.current = setTimeout(() => {
      backoffTimer.current = undefined
      connect()
    }, LOOP_BACKOFF_MS)
  }, [connect, disconnect])

  const handleConnectionFailure = useCallback(() => {
    if (onConnectionFailure) onConnectionFailure()
  }, [onConnectionFailure])

  const handleOnLoop = useCallback(() => {
    reconnectAttempts.current++
    if (reconnectAttempts.current > 2) {
      if (reconnectPolicy === 'retryForever') {
        scheduleReconnect()
      } else {
        disconnect()
        handleConnectionFailure()
      }
    } else {
      connect()
    }
  }, [connect, disconnect, scheduleReconnect, handleConnectionFailure, reconnectPolicy])

  configRef.current = {
    url: url + topic,
    transport: currentTransport,
    fallbackTransport: undefined,
    logLevel: process.env.NODE_ENV === 'development' ? 'info' : 'error',
    contentType: 'application/json; charset=UTF-8',
    trackMessageLength: true,
    reconnectInterval: 200,
    pollingInterval: 100,
    enableProtocol: currentTransport === 'websocket',

    onOpen: handleOnOpen,
    onClose: () => {
      connected.current = false
    },
    onReconnect: () => {
      connected.current = true
      if (rateLimiter.check()) handleOnLoop()
    },
    onError: () => {
      connected.current = false
    },
    onReopen: () => {
      connected.current = true
      if (rateLimiter.check()) handleOnLoop()
    },
    onFailureToReconnect: () => {
      if (reconnectPolicy === 'retryForever') {
        scheduleReconnect()
      } else {
        connected.current = false
        handleConnectionFailure()
      }
    },
    onClientTimeout: () => {
      connected.current = false
    },
    onTransportFailure: () => {
      connected.current = false
      if (configRef.current.transport === 'websocket') {
        configRef.current.transport = 'long-polling'
        setCurrentTransport('long-polling')
        connect()
      } else {
        // eslint-disable-next-line no-console
        console.error(`Atmosphere - Failed websocket and long-polling on topic ${topic}`)
        if (reconnectPolicy === 'retryForever') {
          scheduleReconnect()
        } else {
          disconnect()
          handleConnectionFailure()
        }
      }
    },
    onMessage: handleOnMessage,
  }

  useEffect(() => {
    connect()
  }, [connect])

  // called on destroy component
  useEffect(
    () => () => {
      if (backoffTimer.current) clearTimeout(backoffTimer.current)
      disconnect()
    },
    [disconnect]
  )

  return {
    push,
  }
}
